/**
 * Column settings panel (Task 3) — show/hide + reorder the queue columns, inside
 * the toolbar "Columns" popover. Pure presentation over the prefs the container
 * owns + persists (lib/columnPrefs). Required columns (the order column) can't be
 * hidden. Reorder is via up/down buttons (keyboard-operable; drag-free for
 * reliability). Lists columns in the current display order.
 */
import { Checkbox, Button } from "../../components/ui/index.js";
import type { ColumnPrefs } from "../../lib/columnPrefs.js";
import type { QueueColumn } from "./columns.js";

export function ColumnSettings({
  columns,
  prefs,
  requiredKeys,
  onToggle,
  onMove,
  onReset,
}: {
  columns: QueueColumn[];
  prefs: ColumnPrefs;
  requiredKeys: ReadonlySet<string>;
  onToggle: (key: string) => void;
  onMove: (key: string, dir: -1 | 1) => void;
  onReset: () => void;
}) {
  const byKey = new Map(columns.map((c) => [c.key, c]));
  const hidden = new Set(prefs.hidden);

  return (
    <div className="col-settings">
      <p className="col-settings__title">Columns</p>
      <ul className="col-settings__list">
        {prefs.order.map((key, i) => {
          const col = byKey.get(key);
          if (!col) return null;
          const required = requiredKeys.has(key);
          const visible = required || !hidden.has(key);
          return (
            <li key={key} className="col-settings__row">
              <Checkbox
                ariaLabel={`Show ${col.header} column`}
                checked={visible}
                disabled={required}
                onChange={() => onToggle(key)}
              />
              <span className="col-settings__label">
                {col.header}
                {required && <span className="col-settings__req"> · always</span>}
              </span>
              <span className="col-settings__moves">
                <Button
                  variant="ghost"
                  size="sm"
                  icon="chevronUp"
                  iconOnly
                  aria-label={`Move ${col.header} up`}
                  disabled={i === 0}
                  onClick={() => onMove(key, -1)}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  icon="chevronDown"
                  iconOnly
                  aria-label={`Move ${col.header} down`}
                  disabled={i === prefs.order.length - 1}
                  onClick={() => onMove(key, 1)}
                />
              </span>
            </li>
          );
        })}
      </ul>
      <div className="col-settings__footer">
        <Button variant="ghost" size="sm" icon="rotate" onClick={onReset}>
          Reset to default
        </Button>
      </div>
    </div>
  );
}
